import _defineProperty from "/codebuild/output/src176/src/s3/01/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classCallCheck from "/codebuild/output/src176/src/s3/01/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/codebuild/output/src176/src/s3/01/node_modules/@babel/runtime/helpers/esm/createClass.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { uuid4, timestampInSeconds, logger, dropUndefinedKeys } from '@sentry/utils';
import { DEBUG_BUILD } from '../debug-build.js';
import { TRACE_FLAG_SAMPLED, TRACE_FLAG_NONE, spanToJSON, spanTimeInputToSeconds, spanToTraceHeader, spanToTraceContext } from '../utils/spanUtils.js';

/**
 * Keeps track of finished spans for a given transaction
 * @internal
 * @hideconstructor
 * @hidden
 */
var SpanRecorder = /*#__PURE__*/function () {
  function SpanRecorder() {
    var maxlen = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1000;
    _classCallCheck(this, SpanRecorder);
    this._maxlen = maxlen;
    this.spans = [];
  }

  /**
   * This is just so that we don't run out of memory while recording a lot
   * of spans. At some point we just stop and flush out the start of the
   * trace tree (i.e.the first n spans with the smallest
   * start_timestamp).
   */
  _createClass(SpanRecorder, [{
    key: "add",
    value: function add(span) {
      if (this.spans.length > this._maxlen) {
        span.spanRecorder = undefined;
      } else {
        this.spans.push(span);
      }
    }
  }]);
  return SpanRecorder;
}();
/**
 * Span contains all data about a span
 */
var Span = /*#__PURE__*/function () {
  /**
   * @inheritDoc
   */

  /**
   * Internal keeper of the status
   */

  /**
   * Timestamp in seconds when the span was created.
   */

  /**
   * Timestamp in seconds when the span ended.
   */

  /**
   * @inheritDoc
   */

  /**
   * Tags for the span.
   * @deprecated Use `getSpanAttributes(span)` instead.
   */

  /**
   * Data for the span.
   * @deprecated Use `getSpanAttributes(span)` instead.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  /**
   * List of spans that were finalized
   */

  /**
   * @inheritDoc
   */

  /**
   * The instrumenter that created this span.
   */

  /**
   * The origin of the span, giving context about what created the span.
   */

  /**
   * You should never call the constructor manually, always use `Sentry.startTransaction()`
   * or call `startChild()` on an existing span.
   * @internal
   * @hideconstructor
   * @hidden
   */
  function Span() {
    var spanContext = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Span);
    this._traceId = spanContext.traceId || uuid4();
    this._spanId = spanContext.spanId || uuid4().substring(16);
    this.startTimestamp = spanContext.startTimestamp || timestampInSeconds();
    // eslint-disable-next-line deprecation/deprecation
    this.tags = spanContext.tags ? _objectSpread({}, spanContext.tags) : {};
    // eslint-disable-next-line deprecation/deprecation
    this.data = spanContext.data ? _objectSpread({}, spanContext.data) : {};
    this._attributes = spanContext.attributes ? _objectSpread({}, spanContext.attributes) : {};
    this.instrumenter = spanContext.instrumenter || 'sentry';
    this.origin = spanContext.origin || 'manual';
    // eslint-disable-next-line deprecation/deprecation
    this._name = spanContext.name || spanContext.description;
    if (spanContext.parentSpanId) {
      this.parentSpanId = spanContext.parentSpanId;
    }
    // We want to include booleans as well here
    if ('sampled' in spanContext) {
      this._sampled = spanContext.sampled;
    }
    if (spanContext.op) {
      this.op = spanContext.op;
    }
    if (spanContext.status) {
      this.status = spanContext.status;
    }
    if (spanContext.endTimestamp) {
      this.endTimestamp = spanContext.endTimestamp;
    }
  }

  // This rule conflicts with another eslint rule :(
  /* eslint-disable @typescript-eslint/member-ordering */

  /**
   * An alias for `description` of the Span.
   * @deprecated Use `spanToJSON(span).description` instead.
   */
  _createClass(Span, [{
    key: "name",
    get: function get() {
      return this._name || '';
    }

    /**
     * Update the name of the span.
     * @deprecated Use `spanToJSON(span).description` instead.
     */,
    set: function set(name) {
      this.updateName(name);
    }

    /**
     * Get the description of the Span.
     * @deprecated Use `spanToJSON(span).description` instead.
     */
  }, {
    key: "description",
    get: function get() {
      return this._name;
    }

    /**
     * Get the description of the Span.
     * @deprecated Use `spanToJSON(span).description` instead.
     */,
    set: function set(description) {
      this._name = description;
    }

    /**
     * The ID of the trace.
     * @deprecated Use `spanContext().traceId` instead.
     */
  }, {
    key: "traceId",
    get: function get() {
      return this._traceId;
    }

    /**
     * The ID of the trace.
     * @deprecated You cannot update the traceId of a span after span creation.
     */,
    set: function set(traceId) {
      this._traceId = traceId;
    }

    /**
     * The ID of the span.
     * @deprecated Use `spanContext().spanId` instead.
     */
  }, {
    key: "spanId",
    get: function get() {
      return this._spanId;
    }

    /**
     * The ID of the span.
     * @deprecated You cannot update the spanId of a span after span creation.
     */,
    set: function set(spanId) {
      this._spanId = spanId;
    }

    /**
     * Was this span chosen to be sent as part of the sample?
     * @deprecated Use `isRecording()` instead.
     */
  }, {
    key: "sampled",
    get: function get() {
      return this._sampled;
    }

    /**
     * Was this span chosen to be sent as part of the sample?
     * @deprecated You cannot update the sampling decision of a span after span creation.
     */,
    set: function set(sampled) {
      this._sampled = sampled;
    }

    /**
     * Attributes for the span.
     * @deprecated Use `getSpanAttributes(span)` instead.
     */
  }, {
    key: "attributes",
    get: function get() {
      return this._attributes;
    }

    /**
     * Attributes for the span.
     * @deprecated Use `setAttributes()` instead.
     */,
    set: function set(attributes) {
      this._attributes = attributes;
    }

    /* eslint-enable @typescript-eslint/member-ordering */

    /** @inheritdoc */
  }, {
    key: "spanContext",
    value: function spanContext() {
      var spanId = this._spanId,
        traceId = this._traceId,
        sampled = this._sampled;
      return {
        spanId: spanId,
        traceId: traceId,
        traceFlags: sampled ? TRACE_FLAG_SAMPLED : TRACE_FLAG_NONE
      };
    }

    /**
     * Creates a new `Span` while setting the current `Span.id` as `parentSpanId`.
     * Also the `sampled` decision will be inherited.
     *
     * @deprecated Use `startSpan()`, `startSpanManual()` or `startInactiveSpan()` instead.
     */
  }, {
    key: "startChild",
    value: function startChild(spanContext) {
      var childSpan = new Span(_objectSpread(_objectSpread({}, spanContext), {}, {
        parentSpanId: this._spanId,
        sampled: this._sampled,
        traceId: this._traceId
      }));
      childSpan.spanRecorder = this.spanRecorder;
      if (childSpan.spanRecorder) {
        childSpan.spanRecorder.add(childSpan);
      }
      childSpan.transaction = this.transaction;
      if (DEBUG_BUILD && childSpan.transaction) {
        var opStr = spanContext && spanContext.op || '< unknown op >';
        var nameStr = spanToJSON(childSpan).description || '< unknown name >';
        var idStr = childSpan.transaction.spanContext().spanId;
        var logMessage = "[Tracing] Starting '".concat(opStr, "' span on transaction '").concat(nameStr, "' (").concat(idStr, ").");
        logger.log(logMessage);
        this._logMessage = logMessage;
      }
      return childSpan;
    }

    /**
     * Sets the tag attribute on the current span.
     *
     * Can also be used to unset a tag, by passing `undefined`.
     *
     * @param key Tag key
     * @param value Tag value
     * @deprecated Use `setAttribute()` instead.
     */
  }, {
    key: "setTag",
    value: function setTag(key, value) {
      // eslint-disable-next-line deprecation/deprecation
      this.tags = _objectSpread(_objectSpread({}, this.tags), {}, _defineProperty({}, key, value));
      return this;
    }

    /**
     * Sets the data attribute on the current span
     * @param key Data key
     * @param value Data value
     * @deprecated Use `setAttribute()` instead.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {
    key: "setData",
    value: function setData(key, value) {
      // eslint-disable-next-line deprecation/deprecation
      this.data = _objectSpread(_objectSpread({}, this.data), {}, _defineProperty({}, key, value));
      return this;
    }

    /** @inheritdoc */
  }, {
    key: "setAttribute",
    value: function setAttribute(key, value) {
      if (value === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this._attributes[key];
      } else {
        this._attributes[key] = value;
      }
    }

    /** @inheritdoc */
  }, {
    key: "setAttributes",
    value: function setAttributes(attributes) {
      var _this = this;
      Object.keys(attributes).forEach(function (key) {
        return _this.setAttribute(key, attributes[key]);
      });
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "setStatus",
    value: function setStatus(value) {
      this.status = value;
      return this;
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "setHttpStatus",
    value: function setHttpStatus(httpStatus) {
      // eslint-disable-next-line deprecation/deprecation
      this.setTag('http.status_code', String(httpStatus));
      // eslint-disable-next-line deprecation/deprecation
      this.setData('http.response.status_code', httpStatus);
      var spanStatus = spanStatusfromHttpCode(httpStatus);
      if (spanStatus !== 'unknown_error') {
        this.setStatus(spanStatus);
      }
      return this;
    }

    /** @inheritdoc */
  }, {
    key: "setName",
    value: function setName(name) {
      this.updateName(name);
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "updateName",
    value: function updateName(name) {
      this._name = name;
      return this;
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "isSuccess",
    value: function isSuccess() {
      return this.status === 'ok';
    }

    /**
     * @inheritDoc
     *
     * @deprecated Use `.end()` instead.
     */
  }, {
    key: "finish",
    value: function finish(endTimestamp) {
      return this.end(endTimestamp);
    }

    /** @inheritdoc */
  }, {
    key: "end",
    value: function end(endTimestamp) {
      if (DEBUG_BUILD &&
      // Don't call this for transactions
      this.transaction && this.transaction.spanContext().spanId !== this._spanId) {
        var logMessage = this._logMessage;
        if (logMessage) {
          logger.log(logMessage.replace('Starting', 'Finishing'));
        }
      }
      this.endTimestamp = spanTimeInputToSeconds(endTimestamp);
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "toTraceparent",
    value: function toTraceparent() {
      return spanToTraceHeader(this);
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "toContext",
    value: function toContext() {
      return dropUndefinedKeys({
        data: this._getData(),
        description: this._name,
        endTimestamp: this.endTimestamp,
        op: this.op,
        parentSpanId: this.parentSpanId,
        sampled: this._sampled,
        spanId: this._spanId,
        startTimestamp: this.startTimestamp,
        status: this.status,
        // eslint-disable-next-line deprecation/deprecation
        tags: this.tags,
        traceId: this._traceId
      });
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "updateWithContext",
    value: function updateWithContext(spanContext) {
      // eslint-disable-next-line deprecation/deprecation
      this.data = spanContext.data || {};
      // eslint-disable-next-line deprecation/deprecation
      this._name = spanContext.name || spanContext.description;
      this.endTimestamp = spanContext.endTimestamp;
      this.op = spanContext.op;
      this.parentSpanId = spanContext.parentSpanId;
      this._sampled = spanContext.sampled;
      this._spanId = spanContext.spanId || this._spanId;
      this.startTimestamp = spanContext.startTimestamp || this.startTimestamp;
      this.status = spanContext.status;
      // eslint-disable-next-line deprecation/deprecation
      this.tags = spanContext.tags || {};
      this._traceId = spanContext.traceId || this._traceId;
      return this;
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "getTraceContext",
    value: function getTraceContext() {
      return spanToTraceContext(this);
    }

    /**
     * Get JSON representation of this span.
     */
  }, {
    key: "getSpanJSON",
    value: function getSpanJSON() {
      return dropUndefinedKeys({
        data: this._getData(),
        description: this._name,
        op: this.op,
        parent_span_id: this.parentSpanId,
        span_id: this._spanId,
        start_timestamp: this.startTimestamp,
        status: this.status,
        // eslint-disable-next-line deprecation/deprecation
        tags: Object.keys(this.tags).length > 0 ? this.tags : undefined,
        timestamp: this.endTimestamp,
        trace_id: this._traceId,
        origin: this.origin
      });
    }

    /** @inheritdoc */
  }, {
    key: "isRecording",
    value: function isRecording() {
      return !this.endTimestamp && !!this._sampled;
    }

    /**
     * Convert the object to JSON.
     * @deprecated Use `spanToJSON(span)` instead.
     */
  }, {
    key: "toJSON",
    value: function toJSON() {
      return this.getSpanJSON();
    }

    /**
     * Get the merged data for this span.
     * For now, this combines `data` and `attributes` together,
     * until eventually we can ingest `attributes` directly.
     */
  }, {
    key: "_getData",
    value: function _getData() {
      // eslint-disable-next-line deprecation/deprecation
      var data = this.data,
        attributes = this._attributes;
      var hasData = Object.keys(data).length > 0;
      var hasAttributes = Object.keys(attributes).length > 0;
      if (!hasData && !hasAttributes) {
        return undefined;
      }
      if (hasData && hasAttributes) {
        return _objectSpread(_objectSpread({}, data), attributes);
      }
      return hasData ? data : attributes;
    }
  }]);
  return Span;
}();
/**
 * Converts a HTTP status code into a {@link SpanStatusType}.
 *
 * @param httpStatus The HTTP response status code.
 * @returns The span status or unknown_error.
 */
function spanStatusfromHttpCode(httpStatus) {
  if (httpStatus < 400 && httpStatus >= 100) {
    return 'ok';
  }
  if (httpStatus >= 400 && httpStatus < 500) {
    switch (httpStatus) {
      case 401:
        return 'unauthenticated';
      case 403:
        return 'permission_denied';
      case 404:
        return 'not_found';
      case 409:
        return 'already_exists';
      case 413:
        return 'failed_precondition';
      case 429:
        return 'resource_exhausted';
      default:
        return 'invalid_argument';
    }
  }
  if (httpStatus >= 500 && httpStatus < 600) {
    switch (httpStatus) {
      case 501:
        return 'unimplemented';
      case 503:
        return 'unavailable';
      case 504:
        return 'deadline_exceeded';
      default:
        return 'internal_error';
    }
  }
  return 'unknown_error';
}
export { Span, SpanRecorder, spanStatusfromHttpCode };